/**
 * Badge Unlocked Modal Component
 *
 * Displays a celebration modal when users unlock new badges after completing
 * a breathing session. Shows badge details with animations and confetti-like effects.
 */

import { useEffect } from 'react';
import { UserBadgeWithDetails } from '../lib/badges';
import { getBadgeIcon } from '../lib/badges';

interface BadgeUnlockedModalProps {
  isOpen: boolean;
  badges: UserBadgeWithDetails[];
  onClose: () => void;
}

export default function BadgeUnlockedModal({
  isOpen,
  badges,
  onClose,
}: BadgeUnlockedModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || badges.length === 0) return null;

  const isMultipleBadges = badges.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      style={{ zIndex: 2147483646 }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="badge-unlocked-title"
        className="relative mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 p-6 shadow-2xl sm:mx-0 sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-amber-600 transition hover:bg-amber-100 hover:text-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
          aria-label="Close modal"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="h-6 w-6"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 6l12 12M6 18L18 6"
            />
          </svg>
        </button>

        {/* Celebration Header */}
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="animate-bounce-slow flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-400 text-5xl shadow-xl">
              🎉
            </div>
          </div>

          <h2
            id="badge-unlocked-title"
            className="mb-2 bg-gradient-to-r from-amber-600 via-yellow-600 to-orange-600 bg-clip-text text-3xl font-bold text-transparent"
          >
            {isMultipleBadges
              ? `${badges.length} Badges Unlocked!`
              : 'Badge Unlocked!'}
          </h2>

          <p className="text-base font-medium text-amber-900">
            {isMultipleBadges
              ? 'Amazing progress! You earned multiple achievements!'
              : 'Congratulations on your achievement!'}
          </p>
        </div>

        {/* Badge List */}
        <div className="space-y-4">
          {badges.map((badge) => (
            <div
              key={badge.id}
              className="animate-fade-in-up rounded-xl bg-white/90 p-5 shadow-lg ring-2 ring-amber-200 transition hover:shadow-xl hover:ring-amber-300"
            >
              <div className="flex items-start gap-4">
                {/* Badge Icon */}
                <div className="flex-shrink-0">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 text-4xl shadow-md">
                    {badge.badge?.image_url ? (
                      <img
                        src={badge.badge.image_url}
                        alt={badge.badge?.name || 'badge'}
                        className="h-16 w-16 object-cover"
                      />
                    ) : (
                      badge.badge && getBadgeIcon(badge.badge)
                    )}
                  </div>
                </div>

                {/* Badge Details */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">
                    {badge.badge?.name}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {badge.badge?.description}
                  </p>
                  {badge.badge?.category?.name && (
                    <div className="mt-2 inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                      {badge.badge.category.name}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Button */}
        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full rounded-xl bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:scale-[1.02] hover:from-amber-600 hover:via-yellow-600 hover:to-orange-600 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-amber-500/50 active:scale-[0.98]"
          >
            ✨ Awesome!
          </button>
        </div>

        {/* Footer Message */}
        <p className="mt-4 text-center text-xs text-amber-800/80">
          View all your badges on your profile page
        </p>
      </div>
    </div>
  );
}
